import { useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useAuth } from './useAuth';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

class FatalSseError extends Error {
  constructor(status) {
    super(`SSE HTTP ${status}`);
    this.status = status;
  }
}

export const InsightsSidebar = ({ race, session, onSignInClick }) => {
  const { user, getIdToken } = useAuth();
  const userId = user?.userId ?? null;

  const [bullets, setBullets] = useState([]);
  const [meta, setMeta] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [needsAuth, setNeedsAuth] = useState(false);

  const abortRef = useRef(null);

  useEffect(() => {
    if (!race?.slug || !session) return;
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setBullets([]);
    setMeta(null);
    setError(null);
    setErrorCode(null);
    setNeedsAuth(false);
    setStreaming(true);

    (async () => {
      try {
        const token = await getIdToken();
        if (!token) {
          setNeedsAuth(true);
          setStreaming(false);
          return;
        }
        const url = new URL(`${API_BASE}/api/insights`);
        url.searchParams.set('race', race.slug);
        url.searchParams.set('session', session);

        await fetchEventSource(url.toString(), {
          signal: ctrl.signal,
          headers: { Authorization: `Bearer ${token}` },
          openWhenHidden: true,
          onopen(res) {
            if (res.status === 401) {
              setNeedsAuth(true);
              throw new FatalSseError(401);
            }
            if (!res.ok) throw new FatalSseError(res.status);
          },
          onmessage(ev) {
            if (!ev.event) return;
            let data = {};
            try {
              data = ev.data ? JSON.parse(ev.data) : {};
            } catch {
              return;
            }
            if (ev.event === 'meta') {
              setMeta(data);
            } else if (ev.event === 'bullet') {
              setBullets((prev) => {
                const next = prev.slice();
                next[data.index] = { title: data.title, body: data.body };
                return next;
              });
            } else if (ev.event === 'error') {
              setErrorCode(data.code || 'ERROR');
              setError(data.message || data.code || 'Failed to load insights');
              setStreaming(false);
            } else if (ev.event === 'done') {
              setStreaming(false);
            }
          },
          onclose() {
            setStreaming(false);
          },
          onerror(err) {
            if (err instanceof FatalSseError) throw err;
            setError(err.message || 'Connection error');
            setStreaming(false);
            throw err;
          },
        });
      } catch (err) {
        if (err.name !== 'AbortError') setStreaming(false);
      }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [race?.slug, session, userId]);

  const renderHeader = () => (
    <header className="flex items-center justify-between gap-2 px-5 py-4 border-b border-f1-border shrink-0">
      <h3 className="text-sm font-semibold">AI insights</h3>
      {streaming && (
        <span className="text-[10px] uppercase tracking-wider text-f1-red border border-f1-red/40 rounded px-2 py-0.5">
          Generating
        </span>
      )}
    </header>
  );

  const renderBullets = () =>
    bullets.length > 0 && (
      <ul className="flex flex-col gap-4">
        {bullets.map((b, i) =>
          b ? (
            <li key={i} className="flex flex-col gap-1 animate-fade-in">
              <span className="text-xs uppercase tracking-wider text-f1-red">
                {b.title}
              </span>
              <span className="text-sm text-f1-text leading-snug">{b.body}</span>
            </li>
          ) : null,
        )}
      </ul>
    );

  if (needsAuth) {
    return (
      <aside className="rounded-2xl border border-f1-border bg-f1-surface flex flex-col lg:h-[480px] overflow-hidden">
        {renderHeader()}
        <div className="px-5 py-4 flex flex-col items-start gap-3">
          <p className="text-sm text-f1-muted">
            Sign in to view AI commentary for this session.
          </p>
          <button
            onClick={onSignInClick}
            className="text-xs rounded-md bg-f1-red px-3 py-1.5 font-semibold hover:opacity-90 transition-opacity"
          >
            Sign in
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="rounded-2xl border border-f1-border bg-f1-surface flex flex-col lg:h-[480px] overflow-hidden">
      {renderHeader()}

      <div className="px-5 py-4 flex flex-col gap-4 flex-1 lg:overflow-y-auto">
        {streaming && bullets.length === 0 && (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <div className="h-3 w-24 rounded bg-f1-surface2 animate-pulse" />
                <div className="h-3 w-full rounded bg-f1-surface2 animate-pulse" />
                <div className="h-3 w-3/4 rounded bg-f1-surface2 animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {renderBullets()}

        {error && (
          <div className="flex flex-col gap-2 border-t border-f1-border pt-3">
            <p className="text-sm text-f1-muted">
              {errorCode === 'NO_LAP_DATA'
                ? 'No lap data is available for this session yet.'
                : `Couldn't load insights: ${error}`}
            </p>
          </div>
        )}
      </div>
    </aside>
  );
};
